/**
 * API client for runtime requests to the backend
 */

const API_URL = import.meta.env.VITE_API_URL || ''

interface FetchOptions extends RequestInit {
  params?: Record<string, string>
}

async function fetchApi<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options

  let url = `${API_URL}/api/v1${endpoint}`

  if (params) {
    const searchParams = new URLSearchParams(params)
    url = url + '?' + searchParams.toString()
  }

  // Get tokens from storage
  const accessToken = localStorage.getItem('access_token')
  const cartToken = localStorage.getItem('cart_token')

  // Build headers with auth and cart tokens
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  if (cartToken) {
    headers['X-Cart-Token'] = cartToken
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.detail || 'API error: ' + response.status)
  }

  return response.json()
}

export const api = {
  get: <T>(endpoint: string, params?: Record<string, string>) =>
    fetchApi<T>(endpoint, { method: 'GET', params }),
    
  post: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, { method: 'POST', body: JSON.stringify(data) }),
    
  put: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
    
  delete: <T>(endpoint: string) =>
    fetchApi<T>(endpoint, { method: 'DELETE' }),
}

// Typed API endpoints
export const cartApi = {
  get: () => api.get('/cart'),
  addItem: (productId: number, quantity: number) =>
    api.post('/cart/items', { product_id: productId, quantity }),
  updateItem: (itemId: number, quantity: number) =>
    api.put('/cart/items/' + itemId, { quantity }),
  removeItem: (itemId: number) => api.delete('/cart/items/' + itemId),
  clear: () => api.delete('/cart'),
}

export const checkoutApi = {
  calculate: (data: { cart_token: string; shipping_address?: unknown; discount_code?: string }) =>
    api.post('/checkout/calculate', data),
  createOrder: (data: unknown) => api.post('/checkout/create-order', data),
  createPaymentIntent: (orderId: number, paymentMethod: string) =>
    api.post('/checkout/payment-intent', { order_id: orderId, payment_method: paymentMethod }),
  confirmPayment: (orderId: number, transactionId: string) =>
    api.post(`/checkout/confirm-payment/${orderId}?transaction_id=${transactionId}`, {}),
}

export const authApi = {
  login: async (email: string, password: string, cartToken?: string) => {
    const formData = new FormData()
    formData.append('username', email)
    formData.append('password', password)
    if (cartToken) formData.append('cart_token', cartToken)

    const response = await fetch(`${API_URL}/api/v1/auth/login`, {
      method: 'POST',
      body: formData,
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.detail || 'Login failed')
    }
    return response.json()
  },
  register: (data: { email: string; password: string; username: string; first_name?: string; last_name?: string }) =>
    api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout', {}),
  forgotPassword: (email: string) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, new_password: password }),
}

export const reviewsApi = {
  list: (productId: number) => api.get('/reviews/product/' + productId),
  create: (productId: number, data: unknown) =>
    api.post('/reviews/product/' + productId, data),
}

export const wishlistApi = {
  get: () => api.get('/wishlist'),
  add: (productId: number) => api.post('/wishlist', { product_id: productId }),
  remove: (productId: number) => api.delete('/wishlist/' + productId),
}

export const newsletterApi = {
  subscribe: (email: string) => api.post('/newsletter/subscribe', { email }),
}

export const searchApi = {
  search: (query: string, filters?: Record<string, string>) =>
    api.get('/search', { q: query, ...filters }),
}

// Cart token storage helpers
export const cartTokenStorage = {
  get: () => localStorage.getItem('cart_token'),
  set: (token: string) => localStorage.setItem('cart_token', token),
  clear: () => localStorage.removeItem('cart_token'),
}

// Auth token storage helpers
export const authTokenStorage = {
  getAccessToken: () => localStorage.getItem('access_token'),
  getRefreshToken: () => localStorage.getItem('refresh_token'),
  set: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
  },
  clear: () => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  },
}

// Enhanced fetch with auth
export async function fetchWithAuth<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const token = authTokenStorage.getAccessToken()
  const cartToken = cartTokenStorage.get()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  if (cartToken) {
    headers['X-Cart-Token'] = cartToken
  }

  return fetchApi<T>(endpoint, { ...options, headers })
}
